import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

// ────────────────────────────────────────────────────────────────────────────
//  INTERFACES
// ────────────────────────────────────────────────────────────────────────────

export interface TransbankAmbiente {
  commerceCode: string;
  apiKey: string;
}

export interface TransbankConfig {
  _id?: string;
  integracion: TransbankAmbiente;
  produccion: TransbankAmbiente;
  urlNotify: string;
  /** Activa el bloque Transbank en el entorno de PRODUCCIÓN */
  activoProduccion: boolean;
  /** Activa el bloque Transbank en entorno LOCAL (localhost) */
  activoLocalhost: boolean;
  fechaActualizacion?: string;
}

export interface TransbankTerminal {
  _id?: string;
  nombre: string;
  terminalId: string;
  sucursal: string;
  /** "INTEGRACION" | "PRODUCCION" */
  ambiente: string;
  estado: boolean;
  fechaCreacion?: string;
  fechaActualizacion?: string;
}

export interface TransbankTransaccion {
  _id?: string;
  traceId: string;
  terminalId: string;
  terminalNombre: string;
  commerceCode: string;
  transactionHostId: string;
  ambiente: string;
  totalPayment: number;
  /** "PENDIENTE" | "APROBADO" | "RECHAZADO" | "CANCELADO" */
  estado: string;
  responseCode: number;
  authorizationCode: string;
  cardType: string;
  last4Digits: string;
  sharesNumber: number;
  sharesAmount: number;
  docEntryReference?: string;
  mensajeError?: string;
  reference?: string;
  commerceReference?: string;
  imageClientVoucher?: string;
  montoPropina?: number;
  montoVuelto?: number;
  montoVenta?: number;
  ultimoStatusJson?: string;
  estadoEnVivo?: string;
  fechaCreacion?: string;
  fechaActualizacion?: string;
}

export interface TransbankWebhookLog {
  _id?: string;
  fecha: string;
  ip: string;
  traceId: string;
  commerceCode: string;
  terminalId: string;
  transactionHostId: string;
  authorizationCode: string;
  responseCode: string;
  estado: string;
  headers: string;
  rawBody: string;
  resultadoProcesamiento: string;
  transaccionId: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  SERVICIO
// ────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class TransbankService {

  private readonly api = environment.RUTA_API + '/api/Transbank';
  private readonly token = environment.TOKEN;

  constructor(private _http: HttpClient) {}

  private body(objeto: any = {}) {
    return { token: this.token, objeto };
  }

  // ── Configuración del comercio ──────────────────────────────────────────

  async getConfig(): Promise<any> {
    return this._http.post(`${this.api}/getConfig`, this.body()).toPromise();
  }

  async saveConfig(config: TransbankConfig): Promise<any> {
    return this._http.post(`${this.api}/saveConfig`, this.body(config)).toPromise();
  }

  isLocalhost(): boolean {
    const hostname = (window?.location?.hostname || '').toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost');
  }

  /**
   * Consulta si el módulo Transbank está activo para el entorno actual.
   * Evalúa el hostname real del navegador (window.location.hostname) para elegir:
   *  - res.activoLocalhost (cuando corre en localhost o 127.0.0.1)
   *  - res.activoProduccion (cuando corre en producción ej. terra.indeone.app)
   * Si falla (sin conexión, sin config), devuelve false — nunca bloquea al usuario.
   */
  async isActivo(): Promise<boolean> {
    try {
      const res: any = await this._http.post(`${this.api}/isActivo`, this.body()).toPromise();
      if (!res?.estado) return false;
      return this.isLocalhost() ? (res.activoLocalhost === true) : (res.activoProduccion === true);
    } catch {
      return false;
    }
  }

  // ── Terminales ──────────────────────────────────────────────────────────

  async getTerminales(): Promise<any> {
    return this._http.post(`${this.api}/getTerminales`, this.body()).toPromise();
  }

  async addEditTerminal(terminal: TransbankTerminal): Promise<any> {
    return this._http.post(`${this.api}/addEditTerminal`, this.body(terminal)).toPromise();
  }

  async eliminaTerminal(id: string): Promise<any> {
    return this._http.post(`${this.api}/eliminaTerminal`, this.body({ id })).toPromise();
  }

  // ── Historial y Auditoría ───────────────────────────────────────────────

  async getTransacciones(): Promise<any> {
    return this._http.post(`${this.api}/getTransacciones`, this.body()).toPromise();
  }

  async getWebhookLogs(): Promise<any> {
    return this._http.post(`${this.api}/getWebhookLogs`, this.body()).toPromise();
  }

  // ── Flujo de pago ───────────────────────────────────────────────────────

  /**
   * Envía la orden de cobro al Smart POS.
   * @param terminalId  _id de MongoDB del TransbankTerminal
   * @param monto       Monto en pesos CLP (entero)
   * @param referencia  Referencia interna (ej: número de Nota de Venta)
   * @param docEntryReference DocEntry de la Nota de Venta (para vincular en Mongo)
   */
  async cobrar(terminalId: string, monto: number, referencia: string, docEntryReference?: string): Promise<any> {
    return this._http.post(`${this.api}/cobrar`, this.body({
      terminalId, monto, referencia, docEntryReference
    })).toPromise();
  }

  /**
   * Consulta el resultado de una transacción en nuestra BD.
   * Usar en un intervalo de 3 seg mientras estado === "PENDIENTE".
   */
  async consultarResultado(transaccionId: string): Promise<any> {
    return this._http.post(`${this.api}/resultado`, this.body({ transaccionId })).toPromise();
  }

  /**
   * Anula una transacción aprobada.
   */
  async anular(transaccionId: string): Promise<any> {
    return this._http.post(`${this.api}/anular`, this.body({ transaccionId })).toPromise();
  }

  /**
   * Envía un mensaje de texto para ser impreso en el Smart POS.
   */
  async imprimir(terminalId: string, mensaje: string): Promise<any> {
    return this._http.post(`${this.api}/imprimir`, this.body({ terminalId, mensaje })).toPromise();
  }

  /**
   * Consulta el estado en vivo de cualquier traceId directamente en la API de Transbank.
   */
  async consultarEstadoDirecto(traceId: string, ambiente: string = 'INTEGRACION'): Promise<any> {
    return this._http.post(`${this.api}/consultarEstadoDirecto`, this.body({ traceId, ambiente })).toPromise();
  }

  /**
   * Solicita la cancelación de un traceId directamente en la API de Transbank.
   */
  async cancelarDirecto(traceId: string, ambiente: string = 'INTEGRACION'): Promise<any> {
    return this._http.post(`${this.api}/cancelarDirecto`, this.body({ traceId, ambiente })).toPromise();
  }

  // ── Helpers de UI y Mapeo SAP ──────────────────────────────────────────

  /** Normaliza texto eliminando acentos, espacios y convirtiendo a mayúsculas */
  static normalizar(txt: any): string {
    if (!txt) return '';
    const str = typeof txt === 'string' ? txt : JSON.stringify(txt);
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();
  }

  /** Convierte el cardType de Transbank a un texto amigable y legible */
  static describeCardType(cardType: string): string {
    if (!cardType) return 'Tarjeta Bancaria';
    const ct = this.normalizar(cardType);
    const tipos: Record<string, string> = {
      VD: 'Débito (Redcompra)',
      DB: 'Débito (Redcompra)',
      VN: 'Crédito (Sin cuotas)',
      VC: 'Crédito en cuotas',
      SI: 'Crédito sin interés',
      S2: 'Crédito 2 cuotas sin interés',
      NC: 'Crédito cuotas comercio',
      VP: 'Prepago',
      CR: 'Crédito',
    };
    return tipos[ct] || cardType;
  }

  /** Determina el paymentMethod para TarjetaCreditoDTO (SAP):
   *  "1" = Crédito (cuenta _SYS00000002266), "2" = Débito (cuenta _SYS00000002267)
   *  Maneja todas las variaciones con o sin tildes (DÉBITO / DEBITO / CRÉDITO / CREDITO / REDCOMPRA)
   */
  static cardTypeToPaymentMethod(cardType: string, rawData?: any): string {
    const norm = this.normalizar(`${cardType || ''} ${typeof rawData === 'string' ? rawData : JSON.stringify(rawData || {})}`);
    
    // Débito
    if (
      norm.includes('VD') ||
      norm.includes('DB') ||
      norm.includes('DEBITO') ||
      norm.includes('DEBIT') ||
      norm.includes('REDCOMPRA') ||
      norm.includes('MAESTRO') ||
      norm.includes('CUENTA RUT')
    ) {
      return '2'; // Débito
    }
    
    // Crédito por defecto
    return '1'; // Crédito
  }

  /** Mapea el cardType o marca a un código numérico OCRC para SAP (1=Visa, 2=MasterCard, 3=Amex, etc.)
   *  Inspecciona abreviaturas, glosas, vouchers y payloads crudos sin importar tildes
   */
  static cardTypeToCreditCardCode(cardType: string, rawData?: any): number {
    const norm = this.normalizar(`${cardType || ''} ${typeof rawData === 'string' ? rawData : JSON.stringify(rawData || {})}`);

    // 1. MasterCard (2)
    if (
      norm.includes('MC') ||
      norm.includes('MASTER') ||
      norm.includes('MASTERCARD') ||
      norm.includes('MAESTRO')
    ) {
      return 2; // MASTERCARD (2)
    }

    // 2. American Express (3)
    if (
      norm.includes('AX') ||
      norm.includes('AMEX') ||
      norm.includes('AMERICAN EXPRESS')
    ) {
      return 3; // AMEX (3)
    }

    // 3. Magna (4) / Diners (5)
    if (norm.includes('MAGNA')) return 4;
    if (norm.includes('DINERS')) return 5;

    // 4. Visa (1)
    if (
      norm.includes('VI') ||
      norm.includes('VISA') ||
      norm.includes('ELECTRON')
    ) {
      return 1; // VISA (1)
    }

    // Default general: VISA (1)
    return 1;
  }
}
